/**
 * Dialog Klasifikasi Penerimaan TERPADU (spec 2026-10-22 "TUNTASKAN SINKRONISASI").
 *
 * SATU dialog untuk Antrean Penerimaan (receipt/legacy/project_excess) DAN
 * Mutasi Bank Belum Teralokasi (source_kind="bank_row"):
 * - 8 klasifikasi kanonis + alasan wajib.
 * - Non-A.R. → pilih jenis (hanya tampil saat klasifikasi Non-A.R.).
 * - Alokasi penuh / parsial / split ke beberapa invoice (kind per baris).
 * - Kandidat invoice DIPERKETAT: hanya terverifikasi FAT, tidak dibatalkan,
 *   nilai > 0, outstanding > 0 (kecuali verified_overpayment), bukan draft,
 *   retensi hanya untuk klasifikasi retensi. Identitas lengkap (bukan UUID).
 * - Sumber legacy (project_excess / legacy_implicit): banner amber "Sumber
 *   legacy belum tertaut" + blokir invoice_payment tanpa target (backend juga
 *   menolak).
 * - Tidak memutasi data historis; override + audit trail via backend.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Save, Info, AlertTriangle, Plus, Trash2, Search, Link2 } from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import { formatIDR, fmtDate } from "@/lib/constants";

const CLASSIFICATIONS = [
  { k: "invoice_payment", label: "Pembayaran Invoice Resmi",
    hint: "Alokasikan ke invoice terverifikasi FAT (bukan draft)." },
  { k: "customer_advance", label: "DP / Uang Muka",
    hint: "Deposit sebelum invoice resmi — tidak menaikkan progres resmi." },
  { k: "retention_payment", label: "Pembayaran Retensi",
    hint: "Hanya untuk klaim retensi sah." },
  { k: "cross_project_or_misallocated", label: "Salah Proyek / Lintas Proyek",
    hint: "Perlu dipindahkan — tidak masuk progres resmi proyek ini." },
  { k: "non_ar_receipt", label: "Non-A.R.",
    hint: "Transfer internal, bunga bank, pengembalian, dsb." },
  { k: "duplicate_suspected", label: "Kemungkinan Duplikat",
    hint: "Tandai untuk peninjauan — tidak masuk progres resmi." },
  { k: "verified_overpayment", label: "Kelebihan Pembayaran Terverifikasi",
    hint: "Konfirmasi FAT — ditampilkan terpisah, bukan menambah pembilang progres." },
  { k: "unclassified", label: "Belum Terklasifikasi (default)",
    hint: "Kembalikan ke status default — hapus override FAT." },
];

const NON_AR_KINDS = [
  ["transfer_internal", "Transfer internal"],
  ["pengembalian_dana", "Pengembalian dana"],
  ["setoran_modal", "Setoran modal"],
  ["bunga_bank", "Bunga bank"],
  ["pendapatan_lain", "Pendapatan lain"],
  ["koreksi_bank", "Koreksi bank"],
  ["belum_diketahui", "Belum diketahui"],
];

const SOURCE_LABELS = {
  receipt: "Penerimaan Invoice",
  legacy_implicit: "Legacy — nilai tercatat di invoice (belum tertaut mutasi bank)",
  project_excess: "Selisih Penerimaan Proyek (legacy)",
  bank_row: "Mutasi Bank e-Statement",
};

function fnum(v) {
  const n = Number(String(v ?? "").replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

/** Kandidat DIPERKETAT (spec §3) — backend juga memvalidasi ulang. */
function isValidCandidate(r, forKind) {
  const outstanding = Number(r.outstanding) || 0;
  const gross = Number(r.gross_invoice_amount) || Number(r.cash_due) || 0;
  if (r.verification_state !== "terverifikasi") return false;   // draft/needs_verification ditolak
  if (gross <= 0) return false;                                  // invoice Rp0 ditolak
  if (forKind === "retention_payment") {
    return (r.txn_kind === "klaim_retensi" || r.retention_claim) && outstanding > 0;
  }
  if (forKind === "verified_overpayment") {
    // Kelebihan bayar: invoice boleh sudah lunas (outstanding 0)
    return r.txn_kind !== "klaim_retensi" && !r.retention_claim;
  }
  return r.txn_kind !== "klaim_retensi" && !r.retention_claim && outstanding > 0;
}

export default function ReceiptClassifyDialog({ item, onClose, onSaved }) {
  const isBank = item?.source_kind === "bank_row";
  const isLegacy = item?.source_kind === "project_excess" || item?.is_legacy_implicit;
  const [klass, setKlass] = useState(item?.classification || "unclassified");
  const [reason, setReason] = useState(item?.reason || "");
  const [nonArKind, setNonArKind] = useState(item?.non_ar_kind || "transfer_internal");
  const [allocatedTo, setAllocatedTo] = useState(item?.allocated_to_entry_id || item?.entry_id || "");
  const [splits, setSplits] = useState(() => (item?.split_allocations?.length
    ? item.split_allocations.map((s) => ({ to_entry_id: s.to_entry_id || s.entry_id || "", amount: s.amount, kind: s.kind })) : []));
  const [candidates, setCandidates] = useState([]);
  const [loadingCand, setLoadingCand] = useState(false);
  const [invQuery, setInvQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const needsAllocation = klass === "invoice_payment" || klass === "verified_overpayment";
  const targetKind = klass === "retention_payment" ? "retention_payment" : klass;

  // Kandidat invoice: receipt mode → scope proyek; bank mode → pencarian bebas.
  useEffect(() => {
    if (!needsAllocation && klass !== "retention_payment") { setCandidates([]); return; }
    let cancelled = false;
    setLoadingCand(true);
    const params = new URLSearchParams();
    if (isBank) {
      if (invQuery.trim()) params.set("q", invQuery.trim());
    } else if (item?.project_id) {
      params.set("project_id", item.project_id);
    }
    api.get(`/collection/invoices${params.toString() ? "?" + params.toString() : ""}`)
      .then(({ data }) => {
        if (cancelled) return;
        const rows = (data?.items || []).filter((r) => isValidCandidate(r, targetKind));
        setCandidates(rows.slice(0, 30));
      })
      .catch(() => !cancelled && setCandidates([]))
      .finally(() => !cancelled && setLoadingCand(false));
    return () => { cancelled = true; };
  }, [item?.project_id, klass, isBank, invQuery, needsAllocation, targetKind]);

  const amount = Number(item?.amount || 0);
  const splitTotal = splits.reduce((s, a) => s + fnum(a.amount), 0);
  const splitRemaining = amount - splitTotal;
  const useSplit = splits.length > 0;

  const addSplit = () => setSplits([...splits, { to_entry_id: "", amount: Math.max(0, splitRemaining), kind: klass === "retention_payment" ? "retention_payment" : "invoice_payment" }]);
  const removeSplit = (i) => setSplits(splits.filter((_, idx) => idx !== i));
  const updSplit = (i, patch) => setSplits(splits.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));

  const candLabel = (c) => ({
    id: c.entry_id || c.id,
    title: `${c.invoice_number || "Nomor invoice belum diisi"} · ${c.project_name || "—"}`,
    sub: `${c.client || "—"} · Inv ${fmtDate(c.invoice_date) || "—"} · JT ${fmtDate(c.due_date) || "—"} · Sisa ${formatIDR(c.outstanding ?? 0)} / ${formatIDR(c.gross_invoice_amount ?? 0)} · ${c.verification_state === "terverifikasi" ? "Terverifikasi" : "Draft"}`,
  });

  if (!item) return null;

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (!reason.trim()) { setErr("Alasan wajib diisi."); return; }
    if (klass === "non_ar_receipt" && isBank && !nonArKind) { setErr("Pilih jenis Non-A.R."); return; }
    // Legacy guard (backend juga menolak)
    if (isLegacy && klass === "invoice_payment" && !useSplit && !allocatedTo.trim()) {
      setErr("Sumber legacy belum tertaut — pilih invoice resmi terverifikasi untuk menautkan sumber."); return;
    }
    if (useSplit) {
      if (splitTotal > amount + 0.5) { setErr(`Total split (${formatIDR(splitTotal)}) melebihi nominal/sisa (${formatIDR(amount)}).`); return; }
      if (!isBank && Math.abs(splitRemaining) > 0.5) {
        setErr(`Split tidak seimbang: sisa ${formatIDR(splitRemaining)} belum teralokasi.`); return;
      }
      for (const s of splits) {
        if (fnum(s.amount) <= 0) { setErr("Setiap baris split harus memiliki nominal > 0."); return; }
        if (["invoice_payment", "verified_overpayment", "retention_payment"].includes(s.kind) && !s.to_entry_id?.trim()) {
          setErr("Setiap split ke invoice wajib memilih invoice tujuan."); return;
        }
      }
    } else if ((needsAllocation || klass === "retention_payment") && !allocatedTo.trim() && klass !== "customer_advance"
               && !["non_ar_receipt", "duplicate_suspected", "cross_project_or_misallocated", "unclassified"].includes(klass)) {
      setErr("Pilih invoice tujuan (atau gunakan Split)."); return;
    }
    setSaving(true);
    try {
      if (isBank) {
        await api.post(`/ar/unallocated-receipts/${item.row_id}/resolve`, {
          classification: klass,
          reason: reason.trim(),
          non_ar_kind: klass === "non_ar_receipt" ? nonArKind : null,
          splits: useSplit
            ? splits.map((s) => ({ entry_id: s.to_entry_id?.trim() || null, amount: fnum(s.amount), kind: s.kind }))
            : ((needsAllocation || klass === "retention_payment") && allocatedTo.trim()
                ? [{ entry_id: allocatedTo.trim(), amount, kind: klass }] : []),
        });
      } else {
        await api.post("/ar/receipts/classify", {
          receipt_key: item.receipt_key,
          classification: klass,
          reason: reason.trim(),
          allocated_to_entry_id: needsAllocation && !useSplit ? allocatedTo.trim() : null,
          split_allocations: useSplit ? splits.map((s) => ({ ...s, amount: fnum(s.amount) })) : [],
        });
      }
      onSaved?.();
    } catch (e2) {
      setErr(formatDetail(e2.response?.data?.detail) || "Gagal menyimpan klasifikasi.");
    } finally { setSaving(false); }
  };

  const revert = async () => {
    if (!item.overridden_by_fat) { onClose?.(); return; }
    if (!window.confirm("Batalkan override FAT dan kembalikan ke klasifikasi default engine?")) return;
    setSaving(true);
    try {
      if (isBank) {
        await api.delete(`/ar/unallocated-receipts/${item.row_id}/classify-non-ar`).catch(() => null);
        await api.delete(`/ar/receipts/${encodeURIComponent(item.receipt_key)}/classify`);
      } else {
        await api.delete(`/ar/receipts/${encodeURIComponent(item.receipt_key)}/classify`);
      }
      toast.success("Override FAT dibatalkan.");
      onSaved?.();
    } catch (e2) {
      setErr(formatDetail(e2.response?.data?.detail) || "Gagal membatalkan.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="receipt-classify-dialog">
      <form onSubmit={submit} className="bg-white border border-slate-300 w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
          <h3 className="font-display text-sm uppercase tracking-wider text-[#0B1D3A]">
            {isBank ? "Klasifikasi & Alokasi Mutasi Bank" : "Klasifikasi Penerimaan"}
          </h3>
          <button type="button" onClick={onClose} data-testid="receipt-classify-close"
            className="ml-auto p-1 hover:bg-slate-100"><X size={14} /></button>
        </div>

        <div className="p-4 space-y-3 text-[11px]">
          <div className="border border-slate-200 bg-slate-50/70 p-2 grid grid-cols-2 gap-x-3 gap-y-1">
            <div><span className="text-slate-500">Proyek:</span> <b>{item.project_name || (isBank ? "Belum tertaut" : "—")}</b></div>
            <div><span className="text-slate-500">Client/Pengirim:</span> {item.client || item.sender_name || "Belum diketahui"}</div>
            <div><span className="text-slate-500">Invoice:</span> <code className="text-[10px]">{item.invoice_number || (isLegacy || isBank ? "Belum tertaut" : "—")}</code></div>
            <div><span className="text-slate-500">{isBank ? "Sisa Belum Teralokasi:" : "Nominal:"}</span> <b className="tabular-nums">{formatIDR(amount)}</b></div>
            <div><span className="text-slate-500">Tanggal:</span> {item.date ? fmtDate(item.date) : "Belum diketahui"}</div>
            <div><span className="text-slate-500">Sumber:</span> <code className="text-[10px]">{SOURCE_LABELS[item.source_kind] || item.source_kind || "Penerimaan"}</code></div>
            {isBank && (
              <>
                <div><span className="text-slate-500">Nominal Mutasi:</span> <span className="tabular-nums">{formatIDR(item.full_amount ?? amount)}</span></div>
                <div><span className="text-slate-500">Sudah Teralokasi:</span> <span className="tabular-nums">{formatIDR(item.allocated_amount ?? 0)}</span></div>
                {item.transfer_ref && <div className="col-span-2"><span className="text-slate-500">Berita:</span> <span className="text-[10px]">{item.transfer_ref}</span></div>}
              </>
            )}
          </div>

          {isLegacy && (
            <div className="border border-amber-300 bg-amber-50 px-2 py-1.5 text-[10px] text-amber-900 flex items-start gap-1" data-testid="receipt-classify-legacy-warn">
              <Link2 size={11} className="mt-0.5 shrink-0" />
              <span><b>Sumber legacy belum tertaut.</b> Pilih invoice resmi terverifikasi untuk menautkan sumber sebelum mengklasifikasikan sebagai Pembayaran Invoice Resmi. Klasifikasi lain (DP/retensi/non-A.R.) tetap dapat disimpan dengan alasan.
              {item.source_kind === "project_excess" && item.excess_received != null && (
                <> Selisih: diterima {formatIDR(item.excess_received)} vs dasar {formatIDR(item.excess_cash_due)}.</>
              )}</span>
            </div>
          )}
          {item.is_duplicate_hint && (
            <div className="border border-rose-200 bg-rose-50 px-2 py-1.5 text-[10px] text-rose-800 flex items-start gap-1" data-testid="receipt-classify-dup-warn">
              <AlertTriangle size={11} className="mt-0.5" />
              Mesin menemukan kemungkinan duplikat (tanggal + nominal + bank sama). Konfirmasi hati-hati.
            </div>
          )}
          {item.is_cross_project_hint && (
            <div className="border border-rose-200 bg-rose-50 px-2 py-1.5 text-[10px] text-rose-800 flex items-start gap-1" data-testid="receipt-classify-cross-warn">
              <AlertTriangle size={11} className="mt-0.5" />
              Mutasi bank yang sama ditautkan ke beberapa proyek — kemungkinan salah alokasi.
            </div>
          )}

          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">Klasifikasi</label>
            <div className="mt-1 border border-slate-300 divide-y divide-slate-200 max-h-[180px] overflow-y-auto">
              {CLASSIFICATIONS.map((c) => (
                <label key={c.k} className={`flex items-start gap-2 px-2 py-1.5 cursor-pointer text-[11px] ${klass === c.k ? "bg-orange-50" : "hover:bg-slate-50"}`}
                  data-testid={`receipt-class-opt-${c.k}`}>
                  <input type="radio" name="klass" value={c.k} checked={klass === c.k}
                    onChange={() => setKlass(c.k)} className="mt-0.5" />
                  <div>
                    <div className="font-semibold text-[#0B1D3A]">{c.label}</div>
                    <div className="text-[10px] text-slate-500">{c.hint}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {klass === "non_ar_receipt" && (
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">Jenis Non-A.R.</label>
              <select value={nonArKind} onChange={(e) => setNonArKind(e.target.value)} data-testid="receipt-classify-nonar-kind"
                className="mt-1 h-7 w-full border border-slate-300 px-2 text-[11px]">
                {NON_AR_KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
          )}

          {(needsAllocation || klass === "retention_payment") && !useSplit && (
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">
                Invoice Tujuan *
              </label>
              {isBank && (
                <div className="relative mt-1">
                  <Search size={11} className="absolute left-2 top-2 text-slate-400" />
                  <input type="text" value={invQuery} onChange={(e) => setInvQuery(e.target.value)}
                    data-testid="receipt-classify-inv-search"
                    className="h-7 w-full border border-slate-300 pl-7 pr-2 text-[11px]"
                    placeholder="Cari no. invoice / proyek / client…" />
                </div>
              )}
              <input type="hidden" value={allocatedTo} />
              {loadingCand ? (
                <div className="text-[10px] text-slate-500 italic mt-1">Memuat kandidat…</div>
              ) : candidates.length > 0 ? (
                <div className="mt-1 border border-slate-200 max-h-[150px] overflow-y-auto">
                  <div className="px-2 py-1 bg-slate-50 text-[9px] uppercase tracking-wider text-slate-500 font-semibold sticky top-0">
                    Kandidat Invoice Terverifikasi ({candidates.length})
                  </div>
                  {candidates.map((c) => {
                    const cl = candLabel(c);
                    return (
                      <button key={cl.id} type="button" data-testid={`receipt-classify-cand-${cl.id}`}
                        onClick={() => setAllocatedTo(cl.id)}
                        className={`w-full text-left px-2 py-1 text-[10px] border-t border-slate-100 hover:bg-slate-50 ${cl.id === allocatedTo ? "bg-orange-50" : ""}`}>
                        <div className="font-semibold text-[#0B1D3A]">{cl.title}</div>
                        <div className="text-slate-500 text-[9px]">{cl.sub}</div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="text-[10px] text-slate-400 italic mt-1" data-testid="receipt-classify-no-cand">
                  Tidak ada kandidat memenuhi syarat (terverifikasi, belum lunas, nilai &gt; Rp0).
                </div>
              )}
              {allocatedTo && (
                <div className="mt-1 text-[10px] text-emerald-700" data-testid="receipt-classify-alloc-picked">
                  Invoice terpilih: <code className="font-mono">{candidates.find((c) => (c.entry_id || c.id) === allocatedTo)?.invoice_number || allocatedTo}</code>
                </div>
              )}
            </div>
          )}

          {/* SPLIT */}
          <div className="border border-slate-200">
            <div className="px-2 py-1.5 bg-slate-50 flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">Split Alokasi (opsional)</span>
              <button type="button" onClick={addSplit} data-testid="receipt-classify-add-split"
                className="ml-auto h-6 px-2 text-[9px] uppercase tracking-wider font-semibold border border-slate-300 hover:bg-white flex items-center gap-1">
                <Plus size={10} /> Tambah
              </button>
            </div>
            {splits.length === 0 ? (
              <div className="px-2 py-2 text-[10px] text-slate-400 italic">
                {isBank ? "Tidak ada split — alokasi penuh ke invoice terpilih, atau klasifikasi tanpa alokasi." : "Tidak ada split — alokasi penuh ke invoice di atas."}
              </div>
            ) : (
              <>
                <table className="w-full text-[10px]">
                  <thead>
                    <tr className="bg-slate-50 text-[9px] uppercase tracking-wider text-slate-500">
                      <th className="text-left px-2 py-1">Kind</th>
                      <th className="text-left px-2 py-1">ID Invoice</th>
                      <th className="text-right px-2 py-1">Amount</th>
                      <th className="px-1"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {splits.map((s, i) => (
                      <tr key={i} data-testid={`receipt-classify-split-row-${i}`} className="border-t border-slate-100">
                        <td className="px-2 py-1">
                          <select value={s.kind} onChange={(e) => updSplit(i, { kind: e.target.value })}
                            data-testid={`receipt-classify-split-kind-${i}`}
                            className="h-6 border border-slate-300 text-[10px] px-1 w-full">
                            {CLASSIFICATIONS.map((c) => <option key={c.k} value={c.k}>{c.label}</option>)}
                          </select>
                        </td>
                        <td className="px-2 py-1">
                          <input type="text" value={s.to_entry_id || ""} onChange={(e) => updSplit(i, { to_entry_id: e.target.value })}
                            data-testid={`receipt-classify-split-entry-${i}`}
                            placeholder={["invoice_payment", "verified_overpayment", "retention_payment"].includes(s.kind) ? "ID invoice *" : "—"}
                            className="h-6 border border-slate-300 text-[10px] px-1 font-mono w-full" />
                        </td>
                        <td className="px-2 py-1">
                          <input type="number" value={s.amount} onChange={(e) => updSplit(i, { amount: e.target.value })}
                            data-testid={`receipt-classify-split-amount-${i}`}
                            className="h-6 border border-slate-300 text-[10px] px-1 tabular-nums text-right w-full" min={0} step="any" />
                        </td>
                        <td className="px-1 py-1">
                          <button type="button" onClick={() => removeSplit(i)} data-testid={`receipt-classify-split-remove-${i}`}
                            className="p-0.5 text-rose-700 hover:bg-rose-50"><Trash2 size={11} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="px-2 py-1 border-t border-slate-200 bg-slate-50 flex items-center gap-3 text-[10px]">
                  <span>Total alokasi: <b className="tabular-nums">{formatIDR(splitTotal)}</b></span>
                  <span>Sisa: <b className={`tabular-nums ${splitRemaining < -0.5 || (!isBank && Math.abs(splitRemaining) > 0.5) ? "text-rose-700" : "text-emerald-700"}`} data-testid="receipt-classify-split-remaining">{formatIDR(splitRemaining)}</b></span>
                  <span className="ml-auto text-slate-500">{isBank ? "Sisa mutasi" : "Nominal"}: {formatIDR(amount)}</span>
                </div>
              </>
            )}
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">Alasan FAT *</label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} required rows={2}
              data-testid="receipt-classify-reason"
              className="mt-1 w-full border border-slate-300 px-2 py-1 text-[11px]"
              placeholder="Mis. Konfirmasi dari bank: setoran modal — bukan AR." />
          </div>

          {err && <div className="border border-rose-200 bg-rose-50 px-2 py-1.5 text-[11px] text-rose-700" data-testid="receipt-classify-err">{err}</div>}

          <div className="text-[10px] text-slate-500 italic flex items-start gap-1">
            <Info size={10} className="mt-0.5 shrink-0" />
            Penyimpanan atomik & idempotent — membuka/menyimpan ulang tidak menggandakan receipt. Semua perubahan tercatat di audit trail dengan nama FAT, waktu, serta nilai sebelum/sesudah.
          </div>
        </div>

        <div className="px-4 py-3 border-t border-slate-200 bg-slate-50 flex items-center gap-2">
          {item.overridden_by_fat && (
            <button type="button" onClick={revert} disabled={saving} data-testid="receipt-classify-revert"
              className="h-8 px-3 text-[10px] uppercase tracking-wider font-semibold border border-rose-300 text-rose-700 hover:bg-rose-50">
              Batalkan Override
            </button>
          )}
          <button type="button" onClick={onClose} disabled={saving}
            className="ml-auto h-8 px-3 text-[10px] uppercase tracking-wider font-semibold border border-slate-300 text-slate-700 hover:bg-slate-50">
            Batal
          </button>
          <button type="submit" disabled={saving} data-testid="receipt-classify-submit"
            className="h-8 px-3 text-[10px] uppercase tracking-wider font-bold border border-[#0B1D3A] bg-[#0B1D3A] text-white hover:bg-[#153162] flex items-center gap-1 disabled:opacity-50">
            <Save size={11} /> {saving ? "Menyimpan…" : "Simpan"}
          </button>
        </div>
      </form>
    </div>
  );
}
